// Anhänge aufnehmen und anzeigen.
//
// Der Aufnahmeknopf ist zweigeteilt: „Kamera" öffnet auf dem iPhone direkt die
// Kamera (über capture), „Auswählen" die Mediathek oder den Dateibrowser. Das
// war in der alten App genauso und ist auf dem Telefon der Unterschied
// zwischen zwei und fünf Tipp-Vorgängen.

import { useRef, useState } from "react";
import { api, verkleinereBild, ERLAUBTE_TYPEN, MAX_DATEI } from "../api.ts";
import { dateigroesse } from "../formate.ts";
import { useZustand } from "../zustand.tsx";
import type { Anhang } from "../typen.ts";

/** Eine noch nicht hochgeladene Auswahl — solange der Datensatz nicht existiert. */
export interface Vorgemerkt { blob: Blob; name: string; vorschau: string }

export function pruefeDatei(datei: File): string | null {
  if (datei.size > MAX_DATEI) {
    return `Die Datei ist ${dateigroesse(datei.size)} groß. Mehr als `
      + `${dateigroesse(MAX_DATEI)} nimmt der Server nicht an.`;
  }
  // Leerer Typ kommt bei manchen Android-Browsern vor — dann entscheidet der
  // Server am Inhalt.
  if (datei.type && !ERLAUBTE_TYPEN.includes(datei.type)) {
    return "Nur Bilder (JPEG, PNG, WebP, HEIC) und PDF werden angenommen.";
  }
  return null;
}

/**
 * Auswahlknöpfe. Gibt die verkleinerte Datei nach oben, statt sie selbst
 * hochzuladen — der Aufrufer weiß, ob der Datensatz schon existiert.
 */
export function AnhangWaehlen(
  { aufAuswahl, laeuft }: { aufAuswahl: (v: Vorgemerkt) => void; laeuft?: boolean },
) {
  const { zeigeMeldung } = useZustand();
  const kamera = useRef<HTMLInputElement>(null);
  const mediathek = useRef<HTMLInputElement>(null);

  async function uebernimm(eingabe: HTMLInputElement | null) {
    const datei = eingabe?.files?.[0];
    if (!datei) return;
    const fehler = pruefeDatei(datei);
    if (fehler) { zeigeMeldung(fehler, true); return; }
    try {
      const blob = await verkleinereBild(datei);
      aufAuswahl({ blob, name: datei.name, vorschau: URL.createObjectURL(blob) });
    } catch {
      zeigeMeldung("Die Datei konnte nicht verarbeitet werden.", true);
    } finally {
      // Zurücksetzen, damit dieselbe Datei erneut gewählt werden kann.
      if (eingabe) eingabe.value = "";
    }
  }

  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      <button type="button" className="btn btn-quiet btn-sm" disabled={laeuft}
        onClick={() => kamera.current?.click()}>📷 Kamera</button>
      <button type="button" className="btn btn-quiet btn-sm" disabled={laeuft}
        onClick={() => mediathek.current?.click()}>🖼 Auswählen</button>
      <input ref={kamera} type="file" accept="image/*" capture="environment"
        style={{ display: "none" }} onChange={() => void uebernimm(kamera.current)} />
      <input ref={mediathek} type="file" accept="image/*,application/pdf"
        style={{ display: "none" }} onChange={() => void uebernimm(mediathek.current)} />
    </div>
  );
}

/**
 * Anhangfeld für ein Formularblatt. Zeigt vorhandene Anhänge und nimmt neue
 * auf. Existiert der Datensatz noch nicht (`entitaetId` fehlt), wird die
 * Auswahl nur vorgemerkt und vom Aufrufer nach dem Speichern hochgeladen.
 */
export function AnhangFeld(
  { entitaet, entitaetId, vorhandene, vorgemerkt, aufVormerken }:
  {
    entitaet: string;
    entitaetId?: string;
    vorhandene: Anhang[];
    vorgemerkt: Vorgemerkt | null;
    aufVormerken: (v: Vorgemerkt | null) => void;
  },
) {
  const { aendern } = useZustand();
  const [laeuft, setzeLaeuft] = useState(false);

  async function auswahl(v: Vorgemerkt) {
    if (!entitaetId) { aufVormerken(v); return; }
    setzeLaeuft(true);
    await aendern(() => api.dateiHochladen(v.blob, v.name, entitaet, entitaetId), "Anhang hinzugefügt");
    setzeLaeuft(false);
  }

  return (
    <div className="fld-block">
      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--muted)", marginBottom: 6 }}>
        Anhänge
      </div>

      <div className="anhang-reihe" style={{ marginBottom: 10 }}>
        {vorhandene.map((d) => (
          <span key={d.id} style={{ position: "relative", display: "inline-block" }}>
            {d.mime.startsWith("image/")
              ? <img className="anhang" src={api.dateiAdresse(d.id)} alt={d.dateiname} />
              : <a className="anhang-pdf" href={api.dateiAdresse(d.id)} target="_blank"
                  rel="noreferrer" title={d.dateiname}>📄</a>}
            {entitaetId ? (
              <button type="button" className="x"
                style={{ position: "absolute", top: -6, right: -6, width: 20, height: 20, fontSize: 11 }}
                title="Anhang entfernen"
                onClick={() => {
                  if (!confirm("Diesen Anhang entfernen?")) return;
                  void aendern(() => api.dateiLoesen(d.id, entitaet, entitaetId), "Anhang entfernt");
                }}>✕</button>
            ) : null}
          </span>
        ))}

        {vorgemerkt ? (
          <span style={{ position: "relative", display: "inline-block" }}>
            <img className="anhang" src={vorgemerkt.vorschau} alt="Vorschau"
              style={{ outline: "2px dashed var(--blue)" }} />
            <button type="button" className="x"
              style={{ position: "absolute", top: -6, right: -6, width: 20, height: 20, fontSize: 11 }}
              title="Auswahl verwerfen" onClick={() => aufVormerken(null)}>✕</button>
          </span>
        ) : null}
      </div>

      <AnhangWaehlen aufAuswahl={auswahl} laeuft={laeuft} />

      <p className="hint" style={{ marginTop: 8 }}>
        {entitaetId
          ? "Bilder werden vor dem Senden auf 1100 px verkleinert."
          : "Das Foto wird zusammen mit dem Mangel gespeichert."}
      </p>
    </div>
  );
}
